// Tela de Usuários (Fase 1 — só cadastro; permissões são Fase 2). Só funciona
// na versão web: a senha é hasheada no servidor (bcrypt), que não existe na
// versão de arquivo.

import type { Usuario } from "../../domain/snapshot.ts";
import { apiCriarUsuario, apiRedefinirSenha, euIdAtual } from "../api.ts";
import type { Store } from "../store.ts";
import { clear, el, tabelaPaginada, botao } from "../ui.ts";
import {
  abrirConfirmacao,
  abrirFormModal,
  barraCadastro,
  campo,
  campoObrigatorio,
  inputSenha,
  inputTexto,
  select,
  telaLista,
} from "./_cadastro.ts";
import { EstadoLista } from "./_estado-lista.ts";
import { buscaDaLista, filtroMultiplo, SITUACAO_CADASTRO } from "./_lista-filtros.ts";
import { podeEditarModulo } from "./_permissao.ts";

export function renderUsuarios(
  container: HTMLElement,
  store: Store,
): void | (() => void) {
  if (!__HOSTED__) {
    clear(container);
    container.append(
      el(
        "p",
        { class: "muted lista-vazia" },
        "O cadastro de usuários só funciona na versão web (servidor).",
      ),
    );
    return;
  }

  // filtros, busca e página num estado só (#93): null = todos
  const lista = new EstadoLista({ situacao: null as string[] | null });
  const busca = buscaDaLista(lista, "Buscar por nome ou login…", () => render());
  const fSituacao = filtroMultiplo(lista, "situacao", "Situação", () => render());

  const render = (): void => {
    clear(container);
    const snap = store.get();
    const eu = euIdAtual();
    const podeGravar = podeEditarModulo(store, "configuracao");
    const perfilNome = new Map(snap.perfis.map((p) => [p.id, p.nome]));

    fSituacao.atualizar(SITUACAO_CADASTRO);
    const sitFiltro = fSituacao.selecionados();
    const usuarios = [...snap.usuarios]
      .filter((u) =>
        sitFiltro.size === 0 || sitFiltro.has(u.ativo ? "ativo" : "inativo"),
      )
      .filter((u) => busca.casa(u.nome, u.login ?? ""))
      .sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR"));

    const barra = barraCadastro(
      [fSituacao.element],
      "Novo Usuário",
      () => abrirModalUsuario(null),
      () => {
        lista.limpar();
        render();
      },
      undefined,
      podeGravar,
    );

    if (usuarios.length === 0) {
      container.append(
        telaLista(
          barra,
          busca.elemento(),
          el(
            "p",
            { class: "muted lista-vazia" },
            lista.mensagemVazio({ nenhum: "Nenhum usuário.", filtrado: "Nenhum usuário encontrado." }),
          ),
        ),
      );
      return;
    }

    const linhas = usuarios.map((u) => {
      // sem saber quem sou (apiEu falhou), trata toda linha como "eu" — some
      // com as ações destrutivas até um reload, em vez de mostrá-las quebradas
      const souEu = eu == null || u.id === eu;
      const acoes: Node[] = [];

      if (podeGravar) {
        const editar = botao("secundario", {}, "Editar");
        editar.addEventListener("click", () => abrirModalUsuario(u));

        const senha = botao(
          "secundario",
          { title: "Redefinir senha" },
          "Senha",
        );
        senha.addEventListener("click", () => abrirRedefinirSenha(u));
        acoes.push(editar, senha);
      }

      if (podeGravar && !souEu) {
        const alternar = botao(
          "secundario",
          {},
          u.ativo ? "Inativar" : "Reativar",
        );
        alternar.addEventListener("click", () => {
          void store.executar(
            { agregado: "usuario", cmd: { tipo: "definirAtivo", id: u.id, ativo: !u.ativo } },
            u.ativo ? "Usuário inativado." : "Usuário reativado.",
          );
        });
        const excluir = botao("perigo", {}, "Excluir");
        excluir.addEventListener("click", () => {
          abrirConfirmacao({
            titulo: "Excluir este Usuário?",
            mensagem: `"${u.nome}" perde o acesso ao sistema. Não dá para desfazer — para só tirar o acesso por ora, use "Inativar".`,
            rotuloConfirmar: "Excluir",
            perigo: true,
            aoConfirmar: () =>
              void store.executar(
                { agregado: "usuario", cmd: { tipo: "excluir", id: u.id } },
                "Usuário excluído.",
              ),
          });
        });
        acoes.push(alternar, excluir);
      }

      return el(
        "tr",
        { class: u.ativo ? "" : "inativo" },
        el("td", {}, u.nome, souEu ? el("small", { class: "muted" }, " (você)") : null),
        el("td", { class: "muted" }, u.login ?? "—"),
        el("td", { class: "muted" }, u.perfilId ? (perfilNome.get(u.perfilId) ?? "—") : "—"),
        el(
          "td",
          {},
          u.senhaHash == null
            ? "Sem Senha"
            : u.ativo
              ? el("span", { class: "estado-ativo" }, "Ativo")
              : el("span", { class: "estado-inativo" }, "Inativo"),
        ),
        el("td", { class: "acoes" }, ...acoes),
      );
    });

    container.append(
      telaLista(
        barra,
        busca.elemento(),
        tabelaPaginada(
          "usuarios",
          ["Nome", "Login", "Perfil", "Situação", ""],
          linhas,
          lista.tabela,
          render,
        ),
      ),
    );
  };

  /**
   * `atualId`: o Perfil já atribuído a este usuário sempre entra na lista,
   * mesmo inativo — senão o `<select>` cai sem opção selecionada e Salvar
   * troca o Perfil da pessoa por engano, num edit que nem mexeu nisso.
   */
  const perfilOpcoes = (atualId: string | null): readonly (readonly [string, string])[] => {
    const ps = [...store.get().perfis]
      .filter((p) => p.ativo || p.id === atualId)
      .sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR"));
    return [
      ["", "— Sem Perfil —"],
      ...ps.map((p) => [p.id, p.ativo ? p.nome : `${p.nome} (inativo)`] as const),
    ];
  };

  const abrirModalUsuario = (atual: Usuario | null): void => {
    const nome = inputTexto("Nome da pessoa", atual?.nome ?? "");
    const login = inputTexto("Login (sem espaços)", atual?.login ?? "");
    const perfil = select(perfilOpcoes(atual?.perfilId ?? null), atual?.perfilId ?? "");
    const senha = atual ? null : inputSenha();

    abrirFormModal({
      titulo: atual ? `Editar ${atual.nome}` : "Novo Usuário",
      rotuloSalvar: atual ? "Salvar" : "Criar Usuário",
      campos: () =>
        el(
          "div",
          {},
          campoObrigatorio("Nome", nome),
          campoObrigatorio("Login", login),
          campo("Perfil", perfil),
          senha ? campo("Senha", senha.element) : null,
        ),
      salvar: (aoErro) => {
        const perfilId = perfil.value || null;
        if (atual) {
          return store.executar(
            {
              agregado: "usuario",
              cmd: {
                tipo: "editar",
                id: atual.id,
                nome: nome.value,
                login: login.value,
                perfilId,
              },
            },
            undefined,
            aoErro,
          );
        }
        return store.aplicar(
          () =>
            apiCriarUsuario({
              nome: nome.value,
              login: login.value,
              senha: senha!.value,
              perfilId,
            }),
          "Usuário criado.",
          aoErro,
        );
      },
    });
  };

  const abrirRedefinirSenha = (u: Usuario): void => {
    const senha = inputSenha();
    abrirFormModal({
      titulo: `Redefinir Senha — ${u.nome}`,
      rotuloSalvar: "Redefinir Senha",
      campos: () => campo("Nova senha", senha.element),
      salvar: (aoErro) =>
        store.aplicar(
          () => apiRedefinirSenha(u.id, senha.value),
          "Senha redefinida.",
          aoErro,
        ),
    });
  };

  render();
  return store.subscribe(render);
}
